"use client"

import { useState, useEffect, useRef } from "react"
import { X, Calendar, BarChart3, Users, Building, TrendingUp, FileText } from "lucide-react"
import StatsOverview from "./StatsOverview"
import VillageMap from "./VillageMap"
import RecentGrievances from "./RecentGrievances"
import GrievanceTrend from "./GrievanceTrend"
import useAxiosInstance from "../../../lib/useAxiosInstance"
import peopleBack from "../../../assets/images/people.png"
import polstratLogo from "../../../assets/images/polstrat.png"
import profile from "../../../assets/images/placeholder-icon.png"
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts"

import { DatePicker } from "antd"
const { RangePicker } = DatePicker

const InsightsModal = ({ isOpen, onClose }) => {
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [dateRange, setDateRange] = useState(null)
  const [data, setData] = useState(null)
  const [durationData, setDurationData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingDurationData, setLoadingDurationData] = useState(false)
  const [duration, setDuration] = useState("Past Week")
  const [activeTab, setActiveTab] = useState("insights")
  const axiosInstance = useAxiosInstance()
  const trendsRef = useRef(null)
  const contentRef = useRef(null)
  const user=JSON.parse(localStorage.getItem("user"))
  const themes = localStorage.getItem("themes")
  // const profileUrl = localStorage.getItem("profilePhoto");
  const profileUrl = user?.profileImageLink?.publicUrl

  useEffect(() => {
    if (dateRange) {
      const [startDate, endDate] = dateRange
      setStartDate(startDate.toISOString())
      setEndDate(endDate.toISOString())
    }
  }, [dateRange])

  const scrollToTrends = () => {
    trendsRef.current?.scrollIntoView({ behavior: "smooth" })
    setActiveTab("trends")
  }

  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" })
    }
    setActiveTab("insights")
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await axiosInstance.get(
        `/api/grievances/getGrievanceInsightsPdf?startDate=${startDate}&endDate=${endDate}&duration=${duration}`,
      )
        setData(response?.data)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchDurationData = async () => {
    setLoadingDurationData(true)
    try {
      const response = await axiosInstance.get(
        `/api/grievances/getGrievanceDurationCount?startDate=${startDate}&endDate=${endDate}&duration=${duration}`,
      )
      setDurationData(response?.data)
    } catch (error) {
      console.error("Error fetching duration data:", error)
    } finally {
      setLoadingDurationData(false)
    }
  }

  // Fetch data on modal open (initial load without date filters)
  useEffect(() => {
    if (isOpen) {
      fetchData()
      fetchDurationData()
    }
  }, [isOpen])

  // Fetch duration count when duration is changed
  useEffect(() => {
    if (isOpen && duration) {
      fetchDurationData()
    }
  }, [startDate, endDate, isOpen, duration])

  useEffect(() => {
    if (isOpen) {
      fetchData()
    }
  }, [startDate, endDate, isOpen])

  useEffect(() => {
  if (!isOpen) return;
  if (!contentRef.current) return;
  const handleScroll = () => {
    const contentEl = contentRef.current;
    const trendsEl = trendsRef.current;

    const contentTop = contentEl.scrollTop;
    const trendsTop = trendsEl.offsetTop;
    // console.log("contentEl.clientHeight",contentEl.clientHeight)
    // console.log("contentTop",contentTop)
    // console.log("trendsTop",trendsTop)
    // Add buffer (e.g., 50px) for sensitivity
    if (contentTop + contentEl.clientHeight >= trendsTop) {
      // console.log("TRUE")
      setActiveTab("trends");
    } else {
      // console.log("FALSE")
      setActiveTab("insights");
    }
  };

   const el = contentRef.current;
  el.addEventListener("scroll", handleScroll);

  // Clean up
  return () => {
    if (el) {
      el.removeEventListener("scroll", handleScroll);
    }
  };
}, [isOpen,contentRef.current]);

  if (!isOpen) return null

  const officeBarData =
    data?.data.officeWise.map((office) => ({
      name: office.officeName.length > 15 ? office.officeName.substring(0, 15) + "..." : office.officeName,
      total: office.totalGrievances,
      resolved: office.resolvedGrievances,
      inProgress: office.inProgressGrievances,
    })) || []

  const categoryBarData =
    data?.data.categoryWise.map((item) => ({
      name: item.categoryName.split("-")[0],
      total: item.totalGrievances,
      resolved: item.resolvedGrievances,
      inProgress: item.inProgressGrievances,
    })) || []

  const chartData = [
    {
      name: "Female",
      value: data ? data.data.genderWise.find((item) => item.gender === "female")?.percentage || 0 : [],
      fill: "#bfdbfe",
      // fill: "#ec4899",
      count: data ? data.data.genderWise.find((item) => item.gender === "female")?.count || 0 : [],
    },
    {
      name: "Male",
      value: data ? data.data.genderWise.find((item) => item.gender === "male")?.percentage || 0 : [],
      fill: "#1e3a8a",
      count: data ? data.data.genderWise.find((item) => item.gender === "male")?.count || 0 : [],
    },
  ]
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 insights-modal">
      <div className="bg-white rounded-lg shadow-2xl w-[98vw] h-[95vh] overflow-hidden flex">
        {/* Sidebar Navigation - 20% width */}
        <div className="w-[20%] bg-white border-r border-gray-200 flex flex-col">
          {/* Header in Sidebar with Logo and Back Button */}
          <div style={{  backgroundColor: themes === "darkMode" ? "rgb(0, 43, 72)" : "#004878" }} className="rounded-b-3xl h-[30%]">
          {/* <div style={{ backgroundColor: "#004878" }} className="rounded-b-3xl h-[30%]"> */}
            <div className="w-full h-[30%] flex items-center justify-start p-3 m-0">
              <button
                onClick={onClose}
                className="flex items-center justify-center w-[2rem] h-[2rem] bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors left-3"
              >
                <X size={20} className="text-gray-600" />
              </button>
              <div className="flex justify-center items-center w-full">
                <img src={polstratLogo || "/placeholder.svg"} alt="Polstrat logo" className="w-[10rem] h-[8rem] object-contain" />
              </div>
            </div>
            {/* <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4"> */}
            <div className="p-4 text-white rounded-b-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-black bg-opacity-20 p-2 rounded-lg">
                  <BarChart3 size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Grievance Insights</h2>
                  <p className="text-blue-100 text-sm">Analytics & Reporting</p>
                </div>
              </div>
              {/* Date Range in Sidebar */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-blue-200" />
                  <span className="text-xs font-medium text-blue-100">Date Range:</span>
                </div>
                <RangePicker
                  className="w-full"
                  size="small"
                  allowClear
                  onChange={(dates) => {
                    if (!dates) {
                      setDateRange(null)
                      setStartDate("")
                      setEndDate("")
                    } else {
                      setDateRange(dates)
                    }
                  }}
                  placeholder={["Start Date", "End Date"]}
                />
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="p-4 flex-1">
            <nav className="space-y-2">
              <button
                onClick={scrollToTop}
                className={`transition-colors navigation ${
                  activeTab === "insights" ? "text-white" : (themes !== 'darkMode' ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100" : "text-grey-200 hover:text-grey-600 hover:bg-grey-300")
                }`}
                style={activeTab === "insights" ? {  backgroundColor: themes === "darkMode" ? "rgb(0, 43, 72)" : "#004878" } : {}}
              >
                <BarChart3 size={18} />
                Detailed Insights
              </button>
              <button
                onClick={scrollToTrends}
                className={`transition-colors navigation ${
                  activeTab === "trends" ? "text-white" : (themes !== 'darkMode' ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100" : "text-grey-200 hover:text-grey-600 hover:bg-grey-300")
                }`}
                style={activeTab === "trends" ? {  backgroundColor: themes === "darkMode" ? "rgb(0, 43, 72)" : "#004878" } : {}}
              >
                <TrendingUp size={18} />
                Trends
              </button>
            </nav>
          </div>
          {/* Client Details */}
          <div className="p-4 border-t border-gray-200">
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-8">
                <div className="relative">
                  <img 
                    src={profileUrl || profile} 
                    alt="Profile" 
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                  />
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-color text-sm">
                    {user?.firstName} {user?.lastName}
                  </h3>
                  <p className={`${themes !== 'darkMode'?`text-gray-500`:`text-gray-200`} text-xs`}>{user?.constituencyName}</p>
                </div>
              </div>
            </div>
          </div>
          {/* Background */}
          <div
            className="p-4 relative overflow-hidden h-[6rem]"
            style={{
              // backgroundImage: `url${peopleBack.src || peopleBack}`,
              backgroundImage: `url(${peopleBack})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          </div>
        </div>

        {/* Main Content - 80% width */}
        <div className="w-[80%] flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : data ? (
            <div ref={contentRef} className="overflow-y-auto h-full p-6">
              <div className="space-y-3">
                {/* Stats Overview */}
                <div className="h-[10vh]">
                  <StatsOverview data={data.data.summaryOverview} />
                </div>

                {/* Main Content Grid - No Scrolling Required */}
                <div className="grid grid-cols-12 gap-6 h-[80vh]">
                  {/* Left Column - Charts */}
                  <div className="col-span-8 space-y-4">
                    {/* Top Row - Office and Gender */}
                    <div className="grid grid-cols-12 gap-4 h-[35vh]">
                      {/* Office-wise Distribution */}
                      <div className="col-span-8 bg-white rounded-xl p-2 border border-gray-200">
                        <h3 className="text-sm font-bold text-color mb-3 flex items-center gap-2">
                          <Building size={16} className="text-blue-600" />
                          Office-wise Distribution
                        </h3>
                        <div className="h-[calc(100%-2rem)]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={officeBarData} margin={{ top: 5, right: 10, left: 0, bottom: 10 }}>
                              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} fontSize={11} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
                              <YAxis fontSize={11} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="total" fill="#1e3a8a" name="Total" />
                              <Bar dataKey="resolved" fill="#3b82f6" name="Resolved" />
                              <Bar dataKey="inProgress" fill="#bfdbfe" name="In Progress" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Gender Distribution */}
                      <div className="col-span-4 bg-white rounded-xl p-2 border border-gray-200">
                        <h3 className="text-sm font-bold text-color mb-0 flex items-center gap-2">
                          <Users size={16} className="text-purple-600" />
                          Gender Distribution
                        </h3>
                        <div className="flex flex justify-center h-[calc(100%)] space-y-4">
                          <div className="flex flex-col items-center gap-2">
                            <RadialBarChart
                              width={200}
                              height={200}
                              cx="50%"
                              cy="50%"
                              innerRadius="70%"
                              outerRadius="100%"
                              barSize={10}
                              data={chartData}
                            >
                              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                              <RadialBar minAngle={5} background clockWise dataKey="value" />
                              {chartData.map((item, index) => (
                                <text
                                  key={index}
                                  x="50%"
                                  y={`${50 + index * 15}%`} // Staggering the labels vertically
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  className="text-sm font-bold"
                                  fill={`${themes!=='darkMode'?'#4B5563':'#fff'}`} // Tailwind gray-700 4B5563
                                >
                                  {item.name}: {item.value}%
                                </text>
                              ))}
                            </RadialBarChart>
                            <div className="flex gap-4">
                              {chartData.map((item) => (
                                <div key={item.name} className="flex justify-center items-center gap-1">
                                    <div 
                                      className="w-3 h-3 rounded-full" 
                                      style={{ backgroundColor: item.fill }} 
                                    ></div>
                                  <div className="text-sm font-bold text-color capitalize">{item.name}:</div>
                                  <div className={`text-sm ${themes !== 'darkMode' ? 'text-gray-500':'text-gray-200'}`}>{item.count.toLocaleString()}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Row - Category Distribution */}
                    <div className="h-[40vh] bg-white rounded-xl p-4 border border-gray-200">
                      <h3 className="text-sm font-bold text-color mb-3 flex items-center gap-2">
                        <FileText size={16} className="text-green-600" />
                        Category-wise Distribution
                      </h3>
                      <div className="h-[calc(100%)]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={categoryBarData} margin={{ top: 5, right: 30, left: 20, bottom: 40 }}>
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} fontSize={11} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
                            <YAxis fontSize={11} stroke={themes === 'darkMode' ? '#ffffff' : '#6b7280'}/>
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="total" fill="#1e3a8a" name="Total" />
                            <Bar dataKey="resolved" fill="#3b82f6" name="Resolved" />
                            <Bar dataKey="inProgress" fill="#bfdbfe" name="In Progress" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Map */}
                  <div className="col-span-4 bg-white rounded-xl p-4 border border-gray-200">
                    {/* <h3 className="text-sm font-bold text-gray-900 mb-3">Village Map</h3> */}
                    <div className="h-[100%]">
                      <VillageMap data={data.data.wardWise} />
                    </div>
                  </div>
                </div>

                {/* Trends Section */}
                <div ref={trendsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                  <GrievanceTrend data={durationData?.data} duration={duration} setDuration={setDuration} themes={themes}/>
                  <RecentGrievances grievances={data.data.grievances} />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
                <p>No data available</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default InsightsModal
